import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

interface RootErrorBoundaryProps {
  children?: ReactNode;
}

/**
 * Fallback UI shown when the root error boundary catches an unhandled error.
 *
 * Uses inline styles intentionally — if the app crashes, Tailwind / CSS modules
 * may not have loaded. This guarantees a visible recovery UI in all scenarios.
 */
function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary: () => void;
}) {
  const message = error instanceof Error ? error.message : 'An unexpected error occurred';

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fafafa',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '24px',
      }}
    >
      <div style={{ maxWidth: '480px', textAlign: 'center' }}>
        {/* Icon */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fef2f2',
            marginBottom: '24px',
          }}
        >
          <svg
            aria-hidden="true"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1
          style={{
            fontSize: '24px',
            fontWeight: 600,
            color: '#111827',
            margin: '0 0 8px',
          }}
        >
          Something went wrong
        </h1>

        <p
          style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 8px',
            lineHeight: 1.5,
          }}
        >
          The application encountered an unexpected error. You can try reloading the page or going
          back to the home screen.
        </p>

        {import.meta.env.DEV && (
          <pre
            style={{
              fontSize: '12px',
              color: '#991b1b',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              padding: '12px',
              margin: '16px 0',
              textAlign: 'left',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {message}
          </pre>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
          <button
            type="button"
            onClick={resetErrorBoundary}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              backgroundColor: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => {
              window.location.href = '/';
            }}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#ffffff',
              backgroundColor: '#111827',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Go to home
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Top-level error boundary for the entire application.
 * Reports errors to Sentry and displays a full-page recovery UI.
 *
 * This catches errors that happen outside the router (provider crashes,
 * bootstrap failures, etc.). Route-level errors are handled by
 * TanStack Router's errorComponent / defaultErrorComponent.
 */
export function RootErrorBoundary({ children }: RootErrorBoundaryProps) {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
